import React, { useRef, useState } from "react";
import {
  Bold, Italic, Underline, Heading1, Heading2, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Link2, Minus, List, ListOrdered, Quote, Image as ImageIcon, Table as TableIcon, ChevronDown,
  FileSymlink,
} from "lucide-react";

/**
 * Reusable rich-text formatting toolbar.
 *
 * Renders Bold/Italic/Underline + Font Size + Headings + Bullet/Numbered lists +
 * Alignment (left/center/right/justify) + Insert (Link/Image/Table/Divider) +
 * Undo/Redo. All buttons preserve the editor's text selection by using
 * `onMouseDown={preventDefault}`.
 *
 * Pass an `editorRef` (the contentEditable element) so commands run against
 * the right surface even when the page has multiple editors.
 */
export default function EditorToolbar({
  editorRef,
  onChange,           // called after every command with the new editor HTML
  extraEnd = null,    // optional extra buttons appended on the right side
  className = "",
  testId = "editor-toolbar",
}) {
  const [size, setSize] = useState(11);
  const imgFileRef = useRef(null);

  const cmd = (c, val = null) => {
    editorRef.current?.focus();
    try { document.execCommand("styleWithCSS", false, true); } catch { /* noop */ }
    document.execCommand(c, false, val);
    setTimeout(() => onChange?.(editorRef.current?.innerHTML || ""), 10);
  };

  const insertLink = () => {
    const url = window.prompt("Hyperlink URL (https://…)");
    if (!url) return;
    cmd("createLink", url);
  };

  const insertDivider = () => cmd("insertHTML",
    '<hr style="border:none;border-top:2px solid #cbd5e1;margin:1rem 0;" />');

  const insertPageBreak = () => cmd("insertHTML",
    '<div class="munners-pb" data-page-break="1" contenteditable="false" style="margin:14px 0;border:none;border-top:2px dashed #6366f1;text-align:center;line-height:0;background:transparent;cursor:default;">' +
      '<span style="background:#eef2ff;color:#4338ca;font-size:10px;font-weight:700;padding:2px 10px;border-radius:8px;letter-spacing:0.08em;text-transform:uppercase;">— page break —</span>' +
    '</div><p><br></p>');

  const insertImage = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      // Default width 60% with auto-centered margins so the image sits nicely on
      // the A4 page. User can resize via the floating image toolbar afterwards.
      cmd("insertHTML",
        `<p style="text-align:center;"><img src="${dataUrl}" alt="" style="width:60%;height:auto;display:block;margin:8px auto;" /></p><p><br></p>`);
    };
    reader.readAsDataURL(file);
    if (imgFileRef.current) imgFileRef.current.value = "";
  };

  const insertTable = () => {
    const rows = parseInt(window.prompt("Rows?", "3")) || 3;
    const cols = parseInt(window.prompt("Columns?", "3")) || 3;
    let html = '<table><thead><tr>';
    for (let c = 0; c < cols; c++) html += `<th>Header ${c + 1}</th>`;
    html += '</tr></thead><tbody>';
    for (let r = 0; r < rows; r++) {
      html += "<tr>";
      for (let c = 0; c < cols; c++) html += "<td>&nbsp;</td>";
      html += "</tr>";
    }
    html += "</tbody></table><p><br></p>";
    cmd("insertHTML", html);
  };

  const setListStyle = (style) => {
    cmd("insertUnorderedList");
    setTimeout(() => {
      const sel = window.getSelection();
      let node = sel?.anchorNode;
      while (node && node.nodeName !== "UL" && node.nodeName !== "OL") node = node.parentNode;
      if (node) node.style.listStyleType = style;
      onChange?.(editorRef.current?.innerHTML || "");
    }, 30);
  };

  const applyFontSize = (px) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    editorRef.current?.focus();
    document.execCommand("styleWithCSS", false, true);
    document.execCommand("fontSize", false, "7");
    editorRef.current?.querySelectorAll('font[size="7"]').forEach((f) => {
      const span = document.createElement("span");
      span.style.fontSize = `${px}px`;
      while (f.firstChild) span.appendChild(f.firstChild);
      f.replaceWith(span);
      span.querySelectorAll("[style]").forEach((el) => {
        if (el.style.fontSize) el.style.fontSize = "";
        if (!el.getAttribute("style")) el.removeAttribute("style");
      });
    });
    onChange?.(editorRef.current?.innerHTML || "");
  };

  const commitSize = (v) => {
    const n = Math.max(6, Math.min(200, Math.round(Number(v) || 0)));
    if (!n) return;
    setSize(n);
    applyFontSize(n);
  };

  return (
    <div className={`p-2 rounded-2xl bg-white/95 backdrop-blur border border-slate-200 shadow-sm ${className}`} data-testid={`${testId}-wrap`}>
      <div className="flex flex-wrap gap-1 items-center" data-testid={testId}>
        <ToolBtn onClick={() => cmd("bold")} title="Bold (Ctrl/Cmd+B)"><Bold className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => cmd("italic")} title="Italic (Ctrl/Cmd+I)"><Italic className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => cmd("underline")} title="Underline (Ctrl/Cmd+U)"><Underline className="w-4 h-4" /></ToolBtn>

        {/* Font-size picker (free entry + steppers + presets) */}
        <div className="flex items-center gap-0.5 ml-1 rounded border border-slate-200 bg-white" onMouseDown={(e) => e.preventDefault()}>
          <button type="button" title="Smaller" onMouseDown={(e) => e.preventDefault()} onClick={() => commitSize(size - 1)} className="px-1.5 h-7 text-slate-500 hover:bg-slate-100 rounded-l">−</button>
          <input type="number" min="6" max="200" value={size}
            onChange={(e) => setSize(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitSize(e.target.value); } }}
            onBlur={(e) => commitSize(e.target.value)}
            className="w-12 text-xs text-center outline-none bg-transparent appearance-none"
            title="Font size in px (Enter to apply)" />
          <button type="button" title="Bigger" onMouseDown={(e) => e.preventDefault()} onClick={() => commitSize(size + 1)} className="px-1.5 h-7 text-slate-500 hover:bg-slate-100">+</button>
          <details className="relative">
            <summary onMouseDown={(e) => e.preventDefault()} className="px-1.5 h-7 flex items-center text-slate-500 hover:bg-slate-100 rounded-r cursor-pointer list-none"><ChevronDown className="w-3 h-3" /></summary>
            <div className="absolute top-full right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-1 z-50 w-28 max-h-56 overflow-y-auto">
              {[8, 9, 10, 11, 12, 13, 14, 16, 18, 20, 24, 28, 32, 36, 48, 64, 96].map((s) => (
                <button key={s} type="button" onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => { e.currentTarget.closest("details").open = false; commitSize(s); }}
                  className={`w-full text-left px-2 py-1 rounded hover:bg-slate-100 text-xs ${s === size ? "bg-cyan-50 text-cyan-700 font-semibold" : ""}`}>{s} px</button>
              ))}
            </div>
          </details>
        </div>
        <Sep />

        <ToolBtn onClick={() => cmd("formatBlock", "h1")} title="Heading 1"><Heading1 className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => cmd("formatBlock", "h2")} title="Heading 2"><Heading2 className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => cmd("formatBlock", "blockquote")} title="Blockquote"><Quote className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => cmd("insertUnorderedList")} title="Bulleted list"><List className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => cmd("insertOrderedList")} title="Numbered list"><ListOrdered className="w-4 h-4" /></ToolBtn>
        <select onMouseDown={(e) => e.preventDefault()}
          onChange={(e) => { if (e.target.value) { setListStyle(e.target.value); e.target.value = ""; } }}
          className="text-xs px-1 py-1 rounded bg-white border border-slate-200 cursor-pointer" title="Bullet style">
          <option value="">• Bullet style</option>
          <option value="disc">●  Disc</option>
          <option value="circle">○  Circle</option>
          <option value="square">■  Square</option>
          <option value="decimal">1.  Decimal</option>
          <option value="lower-alpha">a.  Lower alpha</option>
          <option value="upper-alpha">A.  Upper alpha</option>
          <option value="lower-roman">i.  Lower roman</option>
          <option value="upper-roman">I.  Upper roman</option>
        </select>
        <Sep />

        <ToolBtn onClick={() => cmd("justifyLeft")} title="Align left"><AlignLeft className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => cmd("justifyCenter")} title="Align center"><AlignCenter className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => cmd("justifyRight")} title="Align right"><AlignRight className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => cmd("justifyFull")} title="Justify"><AlignJustify className="w-4 h-4" /></ToolBtn>
        <Sep />

        <ToolBtn onClick={insertLink} title="Insert link"><Link2 className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => imgFileRef.current?.click()} title="Insert image"><ImageIcon className="w-4 h-4" /></ToolBtn>
        <input ref={imgFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => insertImage(e.target.files?.[0])} data-testid={`${testId}-image-input`} />
        <ToolBtn onClick={insertTable} title="Insert table"><TableIcon className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={insertDivider} title="Insert divider"><Minus className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={insertPageBreak} title="Insert page break (Ctrl+Enter)" data-testid={`${testId}-page-break`}><FileSymlink className="w-4 h-4" /></ToolBtn>
        <Sep />

        <ToolBtn onClick={() => cmd("undo")} title="Undo (Ctrl/Cmd+Z)">↶</ToolBtn>
        <ToolBtn onClick={() => cmd("redo")} title="Redo (Ctrl/Cmd+Shift+Z)">↷</ToolBtn>
        {extraEnd && (<><Sep />{extraEnd}</>)}
      </div>
    </div>
  );
}

function ToolBtn({ onClick, children, title, ...rest }) {
  return (
    <button type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      className="p-2 rounded hover:bg-slate-100 text-slate-700"
      {...rest}>
      {children}
    </button>
  );
}
function Sep() { return <div className="w-px bg-slate-200 mx-1 self-stretch" />; }
